import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Responsive as ResponsiveGridLayout, Layouts } from 'react-grid-layout';
import './panelContents.scss';
import { getPanelLayouts } from '@/constants/panelLayouts';
import { captureException } from '../../utils/sentry';
import { useUser } from '@/hooks/UserContext';
import { setLocalStorage, getLocalStorage } from '@/utils/localStrage';
import Loading from '@/components/Loading';
import PanelLoadingBoundary from '@/components/PanelLoadingBoundary';
import { FullscreenLockedModal } from '@/components/FullscreenLockedModal';
import { usePanelFullscreen } from './usePanelFullscreen';
import { openPlansWithTracking } from '@/utils/openPlansWithTracking';
import {
    createFullscreenPortal,
    getPanelRenderInfo,
    FullscreenToggleButton,
} from './panelFullscreenHelpers';

// ローカルストレージのキー名
const STORAGE_KEY = 'panel_layout_info';

/**
 * デフォルトのレイアウト設定に、保存された位置情報をマージする
 * @param defaultLayouts ページに表示されるべきパネルのデフォルトレイアウト
 * @param allSavedLayouts ストレージに保存されている全ページのレイアウト
 * @returns マージ済みの、現在のページ用のレイアウト
 */
function mergeSavedIntoDefault(defaultLayouts: Layouts, allSavedLayouts: Layouts): Layouts {
    const result: Layouts = { ...defaultLayouts };
    (Object.keys(result) as Array<keyof Layouts>).forEach((bp) => {
        const savedBpLayout = allSavedLayouts[bp];
        if (!savedBpLayout) return;

        const savedItemsMap = new Map(savedBpLayout.map((item: any) => [item.i, item]));

        result[bp] = defaultLayouts[bp].map((defaultItem) => {
            const savedItem = savedItemsMap.get(defaultItem.i);
            if (savedItem) {
                // 保存された位置情報があれば、デフォルト設定にマージする
                return { ...defaultItem, ...savedItem };
            }
            return defaultItem;
        });
    });
    return result;
}

/**
 * パネルコンテンツ
 */
// Memo panel needs clipping but others require overflow visibility (e.g. date pickers)
const OVERFLOW_HIDDEN_PANEL_IDS = new Set(['hmMemo']);
const FULLSCREEN_ELIGIBLE_PANEL_IDS = new Set([
    'hmTimeClock',
    'hmActivityCalendar',
    'hmSimpleAnalytics',
    'hmMemo',
]);
const FULLSCREEN_MENU_PANEL_IDS = new Set([
    'hmTimeClock',
    'hmActivityCalendar',
    'hmSimpleAnalytics',
]);

function PanelContents({
    componentMap,
}: {
    componentMap: Record<string, React.ReactNode>;
}): ReactElement {
    const { t } = useTranslation('common');
    const { userInfo } = useUser();
    const isPaidPlan: boolean = userInfo?.isPaid === true;
    const referralActive: boolean = userInfo?.referralActive ?? false;
    const [layouts, setLayouts] = useState<Layouts | null>(null);
    const [currentBreakpoint, setCurrentBreakpoint] = useState<string>('pc');
    const [gridWidth, setGridWidth] = useState(0);
    // ベーシックプランのみフルスクリーンを有効化
    const fullscreenEligibleIds = isPaidPlan ? FULLSCREEN_ELIGIBLE_PANEL_IDS : new Set<string>();

    const {
        fullScreenPanelId,
        enterFullScreen: handleEnterFullScreen,
        exitFullScreen: handleExitFullScreen,
    } = usePanelFullscreen(fullscreenEligibleIds);

    // レイアウトの初期化・読み込み
    useEffect(() => {
        const initLayouts = async () => {
            // ユーザー情報が取得できるまで待機
            if (!userInfo) {
                return;
            }

            // 1. isPaidPlan と referralActive に基づいて、全パネルのデフォルトレイアウトを取得
            const fullDefaultLayouts = getPanelLayouts(isPaidPlan, referralActive);

            // 2. このページで表示すべきパネル（componentMapにあるもの）だけを抽出
            const pageSpecificDefaultLayouts: Layouts = {};
            (Object.keys(fullDefaultLayouts) as (keyof typeof fullDefaultLayouts)[]).forEach(
                (bp) => {
                    pageSpecificDefaultLayouts[bp] = fullDefaultLayouts[bp].filter(
                        (item: { i: string }) =>
                            Object.prototype.hasOwnProperty.call(componentMap, item.i),
                    );
                },
            );

            let finalLayouts = pageSpecificDefaultLayouts;

            // 3. ユーザーIDがあれば、保存済みのレイアウトを読み込んでマージ
            if (userInfo?.userId) {
                const savedStr = await getLocalStorage(userInfo.userId, STORAGE_KEY);
                if (savedStr) {
                    try {
                        const allSavedLayouts = JSON.parse(savedStr);
                        finalLayouts = mergeSavedIntoDefault(
                            pageSpecificDefaultLayouts,
                            allSavedLayouts,
                        );
                    } catch (e) {
                        captureException(e as Error, {
                            componentName: 'PanelContents',
                            action: 'initLayouts',
                            message: 'Failed to parse saved layout',
                        });
                    }
                }
            }
            // 4. 計算後の最終レイアウトを state にセット
            setLayouts(finalLayouts);
        };

        initLayouts();
        // isPaidPlan が変わる（無料→有料の判定が変わる）時に再実行
    }, [isPaidPlan, referralActive, componentMap, userInfo]);

    // パネルコンテンツの幅を計算
    useEffect(() => {
        const updateWidth = () => {
            const mainContent = document.querySelector('main');
            if (mainContent) {
                setGridWidth(mainContent.clientWidth);
            }
        };
        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    // 有料プラン可否によって、ドラッグとリサイズを制御
    useEffect(() => {
        const applyGridItemStyles = () => {
            if (!layouts || !currentBreakpoint) return;

            const currentLayouts = layouts[currentBreakpoint as keyof typeof layouts] || [];

            currentLayouts.forEach((layout) => {
                const gridItem = document.querySelector(
                    `.react-grid-item[data-grid-key="${layout.i}"]`,
                );
                if (gridItem) {
                    if (layout.static) {
                        gridItem.classList.add('static');
                    } else {
                        gridItem.classList.remove('static');
                    }

                    if (layout.isDraggable === false) {
                        gridItem.classList.add('no-drag');
                    } else {
                        gridItem.classList.remove('no-drag');
                    }

                    if (layout.isResizable === false) {
                        gridItem.classList.add('no-resize');
                    } else {
                        gridItem.classList.remove('no-resize');
                    }
                }
            });
        };

        // DOMが更新された後に実行
        setTimeout(applyGridItemStyles, 0);
    }, [layouts, currentBreakpoint]);

    // レイアウト変更の保存
    const handleLayoutChange = async (_currentLayout: any[], allChangedLayouts: Layouts) => {
        // 現在の画面のレイアウト（allChangedLayouts）を、保存されている全体のレイアウトにマージ
        if (!userInfo?.userId) return;

        try {
            // ステートを更新してUIに即時反映
            const newLayoutsForState = { ...layouts, ...allChangedLayouts };
            setLayouts(newLayoutsForState as Layouts);

            // 現在保存されている全レイアウトを取得（なければデフォルトレイアウト）
            const savedStr = await getLocalStorage(userInfo.userId, STORAGE_KEY);
            const fullSavedLayouts = savedStr ? JSON.parse(savedStr) : getPanelLayouts(isPaidPlan);

            // このページが管理するパネルのキーリスト
            const currentPagePanelKeys = Object.keys(componentMap);

            // 変更があったブレークポイントのデータだけをマージ
            for (const bp of Object.keys(allChangedLayouts)) {
                const key = bp as keyof Layouts;
                const changedItemsForBp = allChangedLayouts[key]; // 現在のページの変更後アイテム

                // 空のレイアウト配列が渡された場合は、そのブレークポイントの更新をスキップ
                if (!changedItemsForBp || changedItemsForBp.length === 0) continue;

                // 保存されているアイテムから、他ページのアイテムだけを抽出
                const allItemsInStorageForBp = fullSavedLayouts[key] || [];
                const otherPagesItems = allItemsInStorageForBp.filter(
                    (item: { i: string }) => !currentPagePanelKeys.includes(item.i),
                );

                // 他ページのアイテムと、現在のページのアイテムを結合して上書き
                fullSavedLayouts[key] = [...otherPagesItems, ...changedItemsForBp];
            }

            // 保存するオブジェクトを軽量化
            const strippedLayouts = JSON.parse(JSON.stringify(fullSavedLayouts));
            for (const bp in strippedLayouts) {
                strippedLayouts[bp] = strippedLayouts[bp].map((item: any) => ({
                    i: item.i,
                    x: item.x,
                    y: item.y,
                    w: item.w,
                    h: item.h,
                }));
            }

            // 新しい全レイアウトを保存
            await setLocalStorage(userInfo.userId, STORAGE_KEY, JSON.stringify(strippedLayouts));
        } catch (e) {
            captureException(e as Error, {
                componentName: 'PanelContents',
                action: 'handleLayoutChange',
                message: 'Failed to save layout',
            });
        }
    };

    /**
     * ブレークポイント変更時のハンドラー
     */
    const handleBreakpointChange = (newBreakpoint: string) => {
        setCurrentBreakpoint(newBreakpoint);
    };

    // メニューのないパネル用：フルスクリーンロックモーダルの状態
    const [isFullscreenLockedModalOpen, setIsFullscreenLockedModalOpen] = useState(false);
    const [lockedPanelId, setLockedPanelId] = useState<string | null>(null);

    // メニューのないパネル用：フルスクリーンロッククリックイベントを監視してモーダルを開く
    useEffect(() => {
        const handler = (event: Event) => {
            const customEvent = event as CustomEvent<{ panelId?: string }>;
            setLockedPanelId(customEvent.detail?.panelId ?? null);
            setIsFullscreenLockedModalOpen(true);
        };
        window.addEventListener('kotomil:fullscreenLockedClick', handler as EventListener);
        return () =>
            window.removeEventListener(
                'kotomil:fullscreenLockedClick',
                handler as EventListener,
            );
    }, []);

    // ロード中またはレイアウト未設定の場合はローディング表示
    if (!layouts) {
        return <Loading />;
    }

    // レンダリング
    const fullscreenEnterLabel = t('actions.fullscreenEnter');
    const fullscreenExitLabel = t('actions.fullscreenExit');

    const fullScreenPortal = createFullscreenPortal({
        fullScreenPanelId,
        eligiblePanelIds: fullscreenEligibleIds,
        menuPanelIds: FULLSCREEN_MENU_PANEL_IDS,
        componentMap,
        fullscreenExitLabel,
        onEnterFullScreen: handleEnterFullScreen,
        onExitFullScreen: handleExitFullScreen,
    });

    return (
        <>
            <div className="relative w-full max-w-full">
                <ResponsiveGridLayout
                    {...GRID_LAYOUT_CONFIG.default}
                    layouts={layouts}
                    width={gridWidth}
                    className="custom-grid-layout"
                    onBreakpointChange={handleBreakpointChange}
                    onLayoutChange={handleLayoutChange}
                >
                    {/* componentMap に存在するキーのパネルのみをレンダリング */}
                    {layouts[currentBreakpoint as keyof typeof layouts]?.map((layout) => {
                        const panel = componentMap[layout.i];
                        if (!panel) {
                            return null;
                        }
                        const {
                            panelElement,
                            isFullScreenEnabled,
                            isFullScreen,
                            shouldRenderDefaultFullscreenButton,
                        } = getPanelRenderInfo({
                            panelId: layout.i,
                            panel,
                            fullScreenPanelId,
                            // UI上でフルスクリーンを有効にするパネルは常に同じ（課金状態に依存しない）
                            eligiblePanelIds: FULLSCREEN_ELIGIBLE_PANEL_IDS,
                            menuPanelIds: FULLSCREEN_MENU_PANEL_IDS,
                            onEnterFullScreen: handleEnterFullScreen,
                            onExitFullScreen: handleExitFullScreen,
                        });

                        return (
                            <div
                                key={layout.i}
                                data-grid-key={layout.i}
                                className={`panel-shadow border border-gray-200 rounded-lg p-1 flex flex-col justify-center items-center w-full h-full relative ${
                                    isFullScreen && isFullScreenEnabled
                                        ? 'is-fullscreen-active'
                                        : ''
                                }`}
                            >
                                {shouldRenderDefaultFullscreenButton && (
                                    <FullscreenToggleButton
                                        isFullScreen={isFullScreen}
                                        onExit={handleExitFullScreen}
                                        onEnter={() => handleEnterFullScreen(layout.i)}
                                        enterLabel={fullscreenEnterLabel}
                                        exitLabel={fullscreenExitLabel}
                                        isLocked={!isPaidPlan}
                                        onLockedClick={() => {
                                            window.dispatchEvent(
                                                new CustomEvent('kotomil:fullscreenLockedClick', {
                                                    detail: { panelId: layout.i },
                                                }),
                                            );
                                        }}
                                    />
                                )}
                                <PanelLoadingBoundary
                                    wrapperClassName={`flex-1 w-full min-h-0 ${
                                        OVERFLOW_HIDDEN_PANEL_IDS.has(layout.i)
                                            ? 'overflow-hidden'
                                            : ''
                                    }`}
                                    className="flex flex-col justify-center items-center h-full w-full"
                                >
                                    {panelElement}
                                </PanelLoadingBoundary>
                                <div className="panel-drag-handle cursor-move">
                                    {/* dots for handle */}
                                    {[...Array(6)].map((_, i) => (
                                        <div key={i}></div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </ResponsiveGridLayout>
            </div>
            {fullScreenPortal}
            {!isPaidPlan && (
                <FullscreenLockedModal
                    isOpen={isFullscreenLockedModalOpen}
                    onClose={() => setIsFullscreenLockedModalOpen(false)}
                    onGoPlans={() => {
                        setIsFullscreenLockedModalOpen(false);
                        openPlansWithTracking({
                            source: 'fullscreen_panelButton',
                            panelId: lockedPanelId ?? undefined,
                        });
                    }}
                />
            )}
        </>
    );
}

export default PanelContents;

/**
 * グリッドレイアウトの設定
 */
const GRID_LAYOUT_CONFIG = {
    default: {
        breakpoints: { pc: 1024, tablet: 768, mobile: 0 },
        cols: { pc: 12, tablet: 6, mobile: 2 },
        rowHeight: 80,
        containerPadding: [24, 24] as [number, number],
        margin: [24, 24] as [number, number],
        preventCollision: false,
        compactType: 'vertical' as 'vertical' | 'horizontal' | null,
        useCSSTransforms: false,
        draggableHandle: '.panel-drag-handle',
    },
};
